import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { apiClient } from "@/api/client";
import { invalidateStoryMutation } from "@/queries/storyQueryInvalidation";

interface UseSubscriptionMutationsParams {
  getStoredAccessToken: () => string | null;
  sessionKey: string;
}

interface AddPropertyData {
  property_id: string;
  property_name?: string;
  municipality?: string;
  area_hectares?: number;
}

export interface SubscriptionMutations {
  addPropertyToList: (propertyData: AddPropertyData) => Promise<{
    success: boolean;
    error?: string;
    upgrade_required?: boolean;
  }>;
  cancelPropertySubscription: (
    propertyId: string,
    reason?: string,
  ) => Promise<{ success: boolean; error?: string }>;
  recordSimulationUsage: (
    propertyId: string,
  ) => Promise<{ success: boolean; error?: string }>;
}

function usePackageStateInvalidator(sessionKey: string): () => Promise<void> {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    await invalidateStoryMutation(queryClient, {
      kind: "package-change",
      sessionKey,
    });
  }, [queryClient, sessionKey]);
}

export function useSubscriptionMutations({
  getStoredAccessToken,
  sessionKey,
}: UseSubscriptionMutationsParams): SubscriptionMutations {
  const invalidatePackageState = usePackageStateInvalidator(sessionKey);

  const addPropertyToList = useCallback(
    async (propertyData: AddPropertyData) => {
      if (!getStoredAccessToken()) {
        return { success: false, error: "Not authenticated" };
      }

      try {
        await apiClient.post(
          "/api/users/property-subscriptions/add/",
          propertyData,
        );
        await invalidatePackageState();

        return { success: true };
      } catch (err) {
        const axiosErr = err as AxiosError<{ error?: string }>;
        if (axiosErr.response?.status === 402) {
          // Payment required - free tier limit reached
          return {
            success: false,
            error: axiosErr.response.data?.error || "Free tier limit reached",
            upgrade_required: true,
          };
        }

        if (axiosErr.response?.data?.error) {
          return {
            success: false,
            error: axiosErr.response.data.error,
          };
        }

        return {
          success: false,
          error: err instanceof Error ? err.message : "Failed to add property",
        };
      }
    },
    [getStoredAccessToken, invalidatePackageState],
  );

  const cancelPropertySubscription = useCallback(
    async (propertyId: string, reason?: string) => {
      if (!getStoredAccessToken()) {
        return { success: false, error: "Not authenticated" };
      }

      try {
        await apiClient.post(
          `/api/users/property-subscriptions/${propertyId}/cancel/`,
          { reason: reason || "User requested cancellation" },
        );
        await invalidatePackageState();

        return { success: true };
      } catch (err) {
        const axiosErr = err as AxiosError<{ error?: string }>;
        if (axiosErr.response?.data?.error) {
          return {
            success: false,
            error: axiosErr.response.data.error,
          };
        }

        return {
          success: false,
          error:
            err instanceof Error
              ? err.message
              : "Failed to cancel subscription",
        };
      }
    },
    [getStoredAccessToken, invalidatePackageState],
  );

  const recordSimulationUsage = useCallback(
    async (propertyId: string) => {
      if (!getStoredAccessToken()) {
        return { success: false, error: "Not authenticated" };
      }

      try {
        await apiClient.post(
          `/api/users/property-subscriptions/${propertyId}/simulation/`,
        );
        await invalidatePackageState();

        return { success: true };
      } catch (err) {
        const axiosErr = err as AxiosError<{ error?: string }>;
        if (axiosErr.response?.status === 402) {
          // Payment required - simulation limit reached
          return {
            success: false,
            error: axiosErr.response.data?.error || "Simulation limit reached",
          };
        }

        if (axiosErr.response?.data?.error) {
          return {
            success: false,
            error: axiosErr.response.data.error,
          };
        }

        return {
          success: false,
          error:
            err instanceof Error
              ? err.message
              : "Failed to record simulation usage",
        };
      }
    },
    [getStoredAccessToken, invalidatePackageState],
  );

  return {
    addPropertyToList,
    cancelPropertySubscription,
    recordSimulationUsage,
  };
}
